import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const RH_HERO_PRODUCT_ASSET =
  "/assets/products/rh/hero/itaas-rh-product-dashboard.png";
export const RH_HERO_HUMAN_ASSET = "/assets/products/rh/hero/itaas-rh-hr-team.png?v=1";

/** Actors grounded in RH catalog features (employees, org, leave, payroll, recruiting). */
export const RH_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "direction",
    icon: "🏛️",
    titleFr: "Direction",
    titleEn: "Leadership",
    bodyFr: "Pilotez effectifs et indicateurs RH depuis un tableau de bord unique.",
    bodyEn: "Steer headcount and HR indicators from one dashboard.",
  },
  {
    id: "hr",
    icon: "👔",
    titleFr: "Responsables RH",
    titleEn: "HR leaders",
    bodyFr: "Centralisez dossiers employés, congés, présence, recrutement et cycles de paie.",
    bodyEn: "Centralize employee files, leave, attendance, recruiting and payroll cycles.",
  },
  {
    id: "managers",
    icon: "📋",
    titleFr: "Managers",
    titleEn: "Managers",
    bodyFr: "Approuvez les congés et suivez vos équipes dans le parcours RH partagé.",
    bodyEn: "Approve leave and follow your teams in the shared HR journey.",
  },
  {
    id: "admin",
    icon: "🗂️",
    titleFr: "Administration",
    titleEn: "Administration",
    bodyFr: "Structurez l’organisation et les workflows d’embauche paramétrables.",
    bodyEn: "Structure the organization and configurable hiring workflows.",
  },
  {
    id: "employees",
    icon: "🧑‍💼",
    titleFr: "Collaborateurs",
    titleEn: "Employees",
    bodyFr: "Accédez aux demandes de congés et aux informations RH selon votre périmètre.",
    bodyEn: "Access leave requests and HR information within your scope.",
  },
];

/** Qualitative outcomes from catalog valuePropositions — no invented metrics. */
export const RH_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "cycle",
    icon: "🔄",
    titleFr: "Cycle talent unifié",
    titleEn: "Unified talent cycle",
    bodyFr: "Reliez recrutement, intégration, présence, congés et paie dans un même parcours RH.",
    bodyEn: "Connect recruiting, onboarding, attendance, leave and payroll in one HR journey.",
  },
  {
    id: "hiring",
    icon: "🎯",
    titleFr: "Recrutement plus fluide",
    titleEn: "Smoother hiring",
    bodyFr: "Attirez et convertissez les candidats via le portail carrières et des workflows d’embauche paramétrables.",
    bodyEn: "Attract and convert candidates via the careers portal and configurable hiring workflows.",
  },
  {
    id: "ops",
    icon: "⏱️",
    titleFr: "Opérations RH structurées",
    titleEn: "Structured HR operations",
    bodyFr: "Réduisez la dispersion des registres pour la présence, les congés et les dossiers employés.",
    bodyEn: "Reduce scattered registers for attendance, leave and employee files.",
  },
  {
    id: "suite",
    icon: "🧩",
    titleFr: "Intégré à ITaaS Suite",
    titleEn: "Integrated with ITaaS Suite",
    bodyFr: "S’appuie sur IAM et s’articule avec biométrie et portails métier selon vos modules actifs.",
    bodyEn: "Built on IAM and connects with biometrics and vertical portals based on your active modules.",
  },
];

/** Capability cards derived strictly from catalog features. */
export const RH_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Organisation & employés",
    titleEn: "Organization & employees",
    features: [
      "Dossiers employés et organisation",
      "Tableaux de bord RH et indicateurs clés",
    ],
  },
  {
    titleFr: "Présence & congés",
    titleEn: "Attendance & leave",
    features: [
      "Suivi de présence et pointage",
      "Gestion des congés et approbations",
    ],
  },
  {
    titleFr: "Recrutement & carrières",
    titleEn: "Recruiting & careers",
    features: [
      "Pipeline de recrutement complet",
      "Portail carrières / candidat public",
      "Workflows d’embauche paramétrables",
    ],
  },
  {
    titleFr: "Paie & performance",
    titleEn: "Payroll & performance",
    features: [
      "Paie et cycles de rémunération",
      "API paie disponible (périmètre documenté)",
      "Évaluation de la performance",
    ],
  },
];

const RH_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "suite",
    labelFr: "Module de la Suite ITaaS",
    labelEn: "ITaaS Suite module",
  },
  {
    id: "ops",
    labelFr: "Pensé pour ESN et entreprises de services",
    labelEn: "Built for IT services and service companies",
  },
  {
    id: "local",
    labelFr: "Accompagnement ITaaS RDC",
    labelEn: "ITaaS RDC support",
  },
];

export const RH_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: RH_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Interface ITaaS RH — organisation, employés et pilotage RH",
    en: "ITaaS HR interface — organization, employees and HR operations",
  },
  humanImageSrc: RH_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Professionnels RH collaborant sur un ordinateur portable",
    en: "HR professionals collaborating on a laptop",
  },
  visualCaption: {
    fr: "Interface produit ITaaS RH — tableau de bord RH",
    en: "ITaaS HR product interface — HR dashboard",
  },
  heroKicker: {
    fr: "Employés · Congés · Présence · Recrutement · Paie",
    en: "Employees · Leave · Attendance · Recruiting · Payroll",
  },
  audiences: RH_AUDIENCES,
  audienceTitle: {
    fr: "Une plateforme RH pour chaque acteur de l’organisation",
    en: "An HR platform for every stakeholder in the organization",
  },
  outcomes: RH_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour le quotidien des équipes RH et des managers.",
    en: "Practical benefits for day-to-day HR and manager operations.",
  },
  capabilityGroups: RH_CAPABILITY_GROUPS,
  contextTitle: {
    fr: "Adapté aux organisations de services",
    en: "Built for service organizations",
  },
  contextLead: {
    fr: "Conçu pour ESN, agences et entreprises de services — sans affirmation réglementaire non démontrée.",
    en: "Designed for IT services firms, agencies and service companies — without undemonstrated regulatory claims.",
  },
  contextFallbackPoints: [
    {
      fr: "Cycle talent : recrutement → intégration → paie",
      en: "Talent cycle: recruiting → onboarding → payroll",
    },
    {
      fr: "Portail carrières et workflows d’embauche paramétrables",
      en: "Careers portal and configurable hiring workflows",
    },
    {
      fr: "Intégration IAM et articulation avec les modules Suite",
      en: "IAM integration and Suite module articulation",
    },
  ],
  trustBaseChips: RH_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
};
